/* eslint-disable prettier/prettier */
// src/modules/owner/pages/actions/tanqueCheio/components/ImageCropModal.jsx
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  Typography,
  Box,
  IconButton,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from 'common/components/fields/inputImage/canvasUtils';
import { uploadImageToS3, uploadImageFileToS3 } from 'modules/owner/pages/actions/actionsFile';

const ImageCropModal = ({ 
  open, 
  onClose, 
  imageSrc, 
  onCropComplete, 
  stationName,
  aspect = 16/9,
  aspectLabel = "16:9"
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFinalizarEdicao = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fazer o crop da imagem
      const croppedImageBase64 = await getCroppedImg(imageSrc, croppedAreaPixels);
      
      // Converter base64 para File
      const file = dataURLtoFile(croppedImageBase64, 'station-image.jpg');
      
      // Fazer upload para S3 - usar uploadImageToS3 que retorna URL direta
      console.log('Iniciando upload para S3...');
      const result = await uploadImageToS3(croppedImageBase64);
      console.log('Resultado do upload S3:', result);
      
      // result já é a URL direta do S3
      const imageId = result;
      console.log('ImageId (URL) extraído:', imageId);
      
      // Chamar callback com o resultado
      onCropComplete(imageId, file, croppedImageBase64);
      
      // Fechar modal
      onClose();
      
    } catch (error) {
      console.error('Erro ao processar imagem:', error);
    } finally {
      setLoading(false);
    }
  }, [imageSrc, croppedAreaPixels, onCropComplete, onClose]);

  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <>
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.modal + 1 
        }}
        open={loading}
      >
        <Box display="flex" flexDirection="column" alignItems="center">
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2 }}>
            Processando imagem...
          </Typography>
        </Box>
      </Backdrop>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '600px' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6">
            Edite e corte da forma que preferir, na proporção {aspectLabel}
          </Typography>
          <IconButton 
            onClick={handleClose}
            disabled={loading}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {stationName && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Imagem para: {stationName}
            </Typography>
          </Box>
        )}

        <DialogContent sx={{ p: 0, position: 'relative', minHeight: '400px' }}>
          {imageSrc && (
            <Box sx={{ position: 'relative', width: '100%', height: '400px' }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onCropComplete={onCropCompleteCallback}
                onZoomChange={setZoom}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          flexDirection: 'column', 
          gap: 2, 
          p: 3,
          alignItems: 'stretch'
        }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="body2" gutterBottom>
              Ajustar Zoom
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              aria-labelledby="Zoom"
              onChange={(e, zoom) => setZoom(zoom)}
              disabled={loading}
            />
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'flex-end',
            width: '100%'
          }}>
            <Button 
              onClick={handleClose}
              disabled={loading}
              color="secondary"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleFinalizarEdicao}
              variant="contained"
              color="primary"
              disabled={loading || !croppedAreaPixels}
            >
              Finalizar Edição
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

ImageCropModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  imageSrc: PropTypes.string,
  onCropComplete: PropTypes.func.isRequired,
  stationName: PropTypes.string,
  aspect: PropTypes.number,
  aspectLabel: PropTypes.string,
};

export default ImageCropModal;
